/* ==========================================================================
   THF Search
   Desktop dropdown panel + mobile drawer for the header search bar.

   Results come from the Boost PFS suggest API, the same source that powers
   the /search results page, so instant results and the full result set stay
   in agreement. Boost's own suggestion dropdown is suppressed on these
   inputs via the data-disable-instant-search attribute it already honours.
   ========================================================================== */
(function () {
  'use strict';

  if (!window.customElements || !window.fetch) return;

  var SUGGEST_URL_FALLBACK = 'https://services.mybcapps.com/bc-sf-filter/search/suggest';
  var HISTORY_KEY = 'thfRecentSearches';
  var HISTORY_MAX = 6;

  /* ------------------------------------------------------------ utilities */

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function groupDigits(number, decimals, thousands, decimalMark) {
    var fixed = Math.abs(number).toFixed(decimals);
    var parts = fixed.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+$)/g, '$1' + thousands);
    return whole + (parts[1] ? decimalMark + parts[1] : '');
  }

  // Boost returns plain decimal amounts (75.99), not cents.
  function formatMoney(amount, format) {
    var value = Number(amount);
    if (!isFinite(value)) return '';
    if (!format) format = '${{amount}}';

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var name = match ? match[1] : 'amount';
    var out;

    switch (name) {
      case 'amount_no_decimals':
        out = groupDigits(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        out = groupDigits(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        out = groupDigits(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        out = groupDigits(value, 2, "'", '.');
        break;
      default:
        out = groupDigits(value, 2, ',', '.');
    }

    return format.replace(placeholder, out);
  }

  function activeCurrency() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }

  // Markets give us price_min_aud / price_min_nzd alongside the base price.
  function presentmentPrice(product, field) {
    var suffix = activeCurrency().toLowerCase();
    if (suffix && product[field + '_' + suffix] != null) return product[field + '_' + suffix];
    return product[field];
  }

  function sizedImage(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function clearChildren(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  /* Append `text` to `parent`, wrapping every case-insensitive occurrence of
     `term` in a <mark>. Uses text nodes throughout, so API strings are never
     parsed as HTML. */
  function appendHighlighted(parent, text, term) {
    var value = String(text == null ? '' : text);
    var needle = String(term == null ? '' : term).trim();

    if (!needle) {
      parent.appendChild(document.createTextNode(value));
      return;
    }

    var haystack = value.toLowerCase();
    var lowerNeedle = needle.toLowerCase();
    var cursor = 0;
    var index = haystack.indexOf(lowerNeedle, cursor);

    if (index === -1) {
      parent.appendChild(document.createTextNode(value));
      return;
    }

    while (index !== -1) {
      if (index > cursor) {
        parent.appendChild(document.createTextNode(value.slice(cursor, index)));
      }
      var mark = document.createElement('mark');
      mark.textContent = value.slice(index, index + needle.length);
      parent.appendChild(mark);
      cursor = index + needle.length;
      index = haystack.indexOf(lowerNeedle, cursor);
    }

    if (cursor < value.length) {
      parent.appendChild(document.createTextNode(value.slice(cursor)));
    }
  }

  /* ------------------------------------------------------- recent searches */

  function readHistory() {
    try {
      var raw = window.localStorage.getItem(HISTORY_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string'; }) : [];
    } catch (e) {
      return [];
    }
  }

  function saveHistory(term) {
    var value = String(term || '').trim();
    if (!value) return;
    try {
      var list = readHistory().filter(function (t) {
        return t.toLowerCase() !== value.toLowerCase();
      });
      list.unshift(value);
      window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, HISTORY_MAX)));
    } catch (e) {
      /* Private mode or a full quota — recent searches are a nicety, not a
         reason to break the search. */
    }
  }

  /* ------------------------------------------------------------------ API */

  function suggestUrl() {
    var config = window.boostPFSAppConfig || window.boostPFSConfig;
    if (config && config.api && config.api.suggestionUrl) return config.api.suggestionUrl;
    return SUGGEST_URL_FALLBACK;
  }

  function fetchSuggest(query, limits, signal) {
    var params = new URLSearchParams();
    params.set('q', query);
    params.set('shop', window.Shopify && window.Shopify.shop ? window.Shopify.shop : '');
    params.set('event_type', 'suggest');
    params.set('product_limit', limits.products);
    params.set('suggestion_limit', limits.suggestions);
    params.set('collection_limit', limits.collections);
    params.set('page_limit', limits.pages);
    params.set('dym_limit', limits.dym);

    var locale = window.Shopify && window.Shopify.locale;
    if (locale) params.set('locale', locale);
    var currency = activeCurrency();
    if (currency) params.set('currency', currency);

    return fetch(suggestUrl() + '?' + params.toString(), {
      signal: signal,
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Search request failed: ' + response.status);
      return response.json();
    });
  }

  /* ----------------------------------------------------------------- core */

  function SearchCore(host, options) {
    this.host = host;
    this.options = options;
    this.query = '';
    this.controller = null;
    this.pendingRedirect = null;
    this.isOpen = false;

    this.el = {
      form: host.querySelector('[data-thf-form]'),
      input: host.querySelector('[data-thf-input]'),
      clear: host.querySelector('[data-thf-clear]'),
      recentCol: host.querySelector('[data-thf-recent]'),
      recentList: host.querySelector('[data-thf-recent-list]'),
      recentEmpty: host.querySelector('[data-thf-recent-empty]'),
      suggestionsCol: host.querySelector('[data-thf-suggestions]'),
      suggestionList: host.querySelector('[data-thf-suggestion-list]'),
      collectionsCol: host.querySelector('[data-thf-collections]'),
      collectionList: host.querySelector('[data-thf-collection-list]'),
      pagesCol: host.querySelector('[data-thf-pages]'),
      pageList: host.querySelector('[data-thf-page-list]'),
      dym: host.querySelector('[data-thf-dym]'),
      products: host.querySelector('[data-thf-products]'),
      noResults: host.querySelector('[data-thf-no-results]'),
      viewAll: host.querySelector('[data-thf-view-all]')
    };

    // The empty state is rendered server-side, so the panel paints instantly.
    this.defaultProducts = this.el.products ? this.el.products.innerHTML : '';

    this.limits = {
      products: parseInt(host.dataset.productLimit, 10) || 4,
      suggestions: parseInt(host.dataset.suggestionLimit, 10) || 5,
      collections: parseInt(host.dataset.collectionLimit, 10) || 3,
      pages: parseInt(host.dataset.pageLimit, 10) || 3,
      dym: parseInt(host.dataset.dymLimit, 10) || 3
    };
    this.minLength = parseInt(host.dataset.minLength, 10) || 3;
    this.moneyFormat = host.dataset.moneyFormat || '${{amount}}';
    this.searchUrl = host.dataset.searchUrl || '/search';
    this.errorText = host.dataset.errorText || 'Search is temporarily unavailable.';

    this.onQueryChange = debounce(this.runQuery.bind(this), parseInt(host.dataset.debounce, 10) || 250);
  }

  SearchCore.prototype.bind = function (signal) {
    var self = this;
    var input = this.el.input;
    if (!input) return;

    input.addEventListener('input', function () {
      self.query = input.value;
      self.reflectValue();
      if (typeof self.onInputActivity === 'function') self.onInputActivity();
      self.onQueryChange(input.value);
    }, { signal: signal });

    if (this.el.form) {
      this.el.form.addEventListener('submit', function (event) {
        var term = input.value.trim();
        if (!term) {
          event.preventDefault();
          return;
        }
        saveHistory(term);
        if (self.pendingRedirect && self.pendingRedirect.query === term) {
          event.preventDefault();
          window.location.href = self.pendingRedirect.redirect;
        }
      }, { signal: signal });
    }

    if (this.el.clear) {
      this.el.clear.addEventListener('click', function () {
        input.value = '';
        self.query = '';
        self.reflectValue();
        self.runQuery('');
        input.focus();
      }, { signal: signal });
    }

    if (this.el.viewAll) {
      this.el.viewAll.addEventListener('click', function () {
        saveHistory(input.value.trim());
      }, { signal: signal });
    }

    // Arrow keys walk the list the focus currently sits in.
    this.host.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      var active = document.activeElement;

      if (active === input && event.key === 'ArrowDown') {
        var first = self.host.querySelector('.thf-search__list a, .thf-search__product');
        if (first) {
          event.preventDefault();
          first.focus();
        }
        return;
      }

      var list = active && active.closest ? active.closest('.thf-search__list, .thf-search__products') : null;
      if (!list) return;
      var links = Array.prototype.slice.call(list.querySelectorAll('a'));
      var index = links.indexOf(active);
      if (index === -1) return;
      event.preventDefault();
      var next = event.key === 'ArrowDown'
        ? links[index + 1] || links[0]
        : links[index - 1] || links[links.length - 1];
      next.focus();
    }, { signal: signal });

    this.renderRecent();
    this.reflectValue();
  };

  /* Opening with a term already in the field (the results page pre-fills it)
     should show that term's results, not the empty state. */
  SearchCore.prototype.syncToInput = function () {
    var value = this.el.input ? this.el.input.value.trim() : '';
    if (value.length >= this.minLength) this.runQuery(value);
  };

  SearchCore.prototype.reflectValue = function () {
    var wrapper = this.el.input ? this.el.input.closest('.thf-search-bar') : null;
    if (wrapper) wrapper.dataset.hasValue = this.el.input.value.trim() ? 'true' : 'false';
  };

  SearchCore.prototype.renderRecent = function () {
    if (!this.el.recentList) return;
    var terms = readHistory();
    clearChildren(this.el.recentList);

    if (this.el.recentEmpty) this.el.recentEmpty.hidden = terms.length > 0;

    for (var i = 0; i < terms.length; i++) {
      this.el.recentList.appendChild(this.termItem(terms[i], ''));
    }
  };

  SearchCore.prototype.termItem = function (term, highlight) {
    var li = document.createElement('li');
    var link = document.createElement('a');
    link.href = this.searchUrl + '?q=' + encodeURIComponent(term);
    appendHighlighted(link, term, highlight);
    link.addEventListener('click', function () { saveHistory(term); });
    li.appendChild(link);
    return li;
  };

  SearchCore.prototype.linkItem = function (label, href) {
    var li = document.createElement('li');
    var link = document.createElement('a');
    link.href = href;
    link.textContent = label;
    li.appendChild(link);
    return li;
  };

  SearchCore.prototype.productCard = function (product) {
    var card = document.createElement('a');
    card.className = 'thf-search__product';
    card.href = '/products/' + product.handle;

    var media = document.createElement('span');
    media.className = 'thf-search__product-media';
    var info = (product.images_info && product.images_info[0]) || null;
    if (info && info.src) {
      var img = document.createElement('img');
      img.src = sizedImage(info.src, 400);
      img.srcset = sizedImage(info.src, 200) + ' 200w, ' +
                   sizedImage(info.src, 400) + ' 400w, ' +
                   sizedImage(info.src, 600) + ' 600w';
      img.sizes = '(max-width: 798px) 45vw, 220px';
      img.alt = info.alt || product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      media.appendChild(img);
    }
    card.appendChild(media);

    var title = document.createElement('span');
    title.className = 'thf-search__product-title';
    title.textContent = product.title || '';
    card.appendChild(title);

    var price = document.createElement('span');
    price.className = 'thf-search__product-price';
    var now = presentmentPrice(product, 'price_min');
    var was = presentmentPrice(product, 'compare_at_price_min');

    if (was && Number(was) > Number(now)) {
      var oldPrice = document.createElement('s');
      oldPrice.textContent = formatMoney(was, this.moneyFormat);
      var newPrice = document.createElement('ins');
      newPrice.textContent = formatMoney(now, this.moneyFormat);
      price.appendChild(oldPrice);
      price.appendChild(newPrice);
    } else {
      price.textContent = formatMoney(now, this.moneyFormat);
    }
    card.appendChild(price);

    return card;
  };

  SearchCore.prototype.showSkeletons = function () {
    if (!this.el.products) return;
    clearChildren(this.el.products);
    this.el.products.hidden = false;
    for (var i = 0; i < this.limits.products; i++) {
      var card = document.createElement('span');
      card.className = 'thf-search__product thf-search__skeleton';
      card.innerHTML = '<span class="thf-search__product-media"></span>' +
                       '<span class="thf-search__product-title"></span>' +
                       '<span class="thf-search__product-price"></span>';
      this.el.products.appendChild(card);
    }
  };

  SearchCore.prototype.restoreDefaults = function () {
    this.host.removeAttribute('data-query');
    if (this.el.products) {
      this.el.products.innerHTML = this.defaultProducts;
      this.el.products.hidden = !this.defaultProducts;
    }
    if (this.el.suggestionsCol) this.el.suggestionsCol.hidden = true;
    if (this.el.collectionsCol) this.el.collectionsCol.hidden = true;
    if (this.el.pagesCol) this.el.pagesCol.hidden = true;
    if (this.el.dym) this.el.dym.hidden = true;
    if (this.el.viewAll) this.el.viewAll.hidden = true;
    if (this.el.noResults) this.el.noResults.hidden = true;
    this.pendingRedirect = null;
  };

  SearchCore.prototype.runQuery = function (raw) {
    var self = this;
    var query = String(raw || '').trim();

    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }

    if (query.length < this.minLength) {
      this.restoreDefaults();
      return;
    }

    // Lets the drawer reorder itself so results aren't buried under the
    // browse-the-store chip rows once a query is live.
    this.host.dataset.query = 'active';

    this.showSkeletons();
    this.controller = new AbortController();

    fetchSuggest(query, this.limits, this.controller.signal)
      .then(function (data) {
        self.controller = null;
        self.render(data, query);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        self.controller = null;
        self.renderError();
      });
  };

  SearchCore.prototype.renderError = function () {
    if (this.el.products) {
      clearChildren(this.el.products);
      this.el.products.hidden = true;
    }
    if (this.el.noResults) {
      this.el.noResults.textContent = this.errorText;
      this.el.noResults.hidden = false;
    }
    if (this.el.viewAll) this.el.viewAll.hidden = true;
  };

  SearchCore.prototype.render = function (data, query) {
    var i;
    var products = data.products || [];
    var suggestions = data.suggestions || [];
    var collections = data.collections || [];
    var pages = data.pages || [];
    var dym = data.did_you_mean || [];

    this.pendingRedirect = data.redirect
      ? { query: query, redirect: data.redirect }
      : null;

    // Term suggestions
    if (this.el.suggestionList) {
      clearChildren(this.el.suggestionList);
      for (i = 0; i < suggestions.length; i++) {
        var suggestion = suggestions[i];
        var term = typeof suggestion === 'string' ? suggestion : (suggestion.suggest || suggestion.label || '');
        if (term) this.el.suggestionList.appendChild(this.termItem(term, query));
      }
      if (this.el.suggestionsCol) this.el.suggestionsCol.hidden = suggestions.length === 0;
    }

    // Collections
    if (this.el.collectionList) {
      clearChildren(this.el.collectionList);
      for (i = 0; i < collections.length; i++) {
        this.el.collectionList.appendChild(
          this.linkItem(collections[i].title, '/collections/' + collections[i].handle)
        );
      }
      if (this.el.collectionsCol) this.el.collectionsCol.hidden = collections.length === 0;
    }

    // Pages
    if (this.el.pageList) {
      clearChildren(this.el.pageList);
      for (i = 0; i < pages.length; i++) {
        this.el.pageList.appendChild(this.linkItem(pages[i].title, '/pages/' + pages[i].handle));
      }
      if (this.el.pagesCol) this.el.pagesCol.hidden = pages.length === 0;
    }

    // Did you mean
    if (this.el.dym) {
      clearChildren(this.el.dym);
      var suggestedTerm = dym.length ? (typeof dym[0] === 'string' ? dym[0] : dym[0].suggest) : '';
      if (suggestedTerm && products.length === 0) {
        this.el.dym.appendChild(document.createTextNode('Did you mean '));
        var dymLink = document.createElement('a');
        dymLink.href = this.searchUrl + '?q=' + encodeURIComponent(suggestedTerm);
        dymLink.textContent = suggestedTerm;
        this.el.dym.appendChild(dymLink);
        this.el.dym.appendChild(document.createTextNode('?'));
        this.el.dym.hidden = false;
      } else {
        this.el.dym.hidden = true;
      }
    }

    // Products
    if (this.el.products) {
      clearChildren(this.el.products);
      for (i = 0; i < products.length; i++) {
        this.el.products.appendChild(this.productCard(products[i]));
      }
      this.el.products.hidden = products.length === 0;
    }

    if (this.el.noResults) {
      this.el.noResults.textContent = 'No results found';
      this.el.noResults.hidden = products.length > 0;
    }

    // View all
    if (this.el.viewAll) {
      var total = Number(data.total_product || 0);
      if (total > 0) {
        this.el.viewAll.href = this.searchUrl + '?q=' + encodeURIComponent(query);
        this.el.viewAll.textContent = 'View all results (' + total.toLocaleString() + ')';
        this.el.viewAll.hidden = false;
      } else {
        this.el.viewAll.hidden = true;
      }
    }
  };

  /* ------------------------------------------------- rotating placeholder */

  function startPlaceholderRotation(input, terms, signal) {
    if (!input || !terms.length) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var index = 0;
    var timer = setInterval(function () {
      if (input.value || document.activeElement === input) return;
      index = (index + 1) % terms.length;
      input.placeholder = 'Search ' + terms[index] + '...';
    }, 3000);

    if (signal) {
      signal.addEventListener('abort', function () { clearInterval(timer); });
    }
  }

  /* The mobile pill is a button, not an input, so it rotates its own label.
     Its terms live on the element because it sits in the header, outside both
     custom elements. */
  function startPillRotation() {
    var pills = document.querySelectorAll('[data-thf-placeholder-rotator]');
    if (!pills.length) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    Array.prototype.forEach.call(pills, function (pill) {
      var terms = parseTerms(pill);
      if (terms.length < 2) return;

      var index = 0;
      setInterval(function () {
        index = (index + 1) % terms.length;
        pill.dataset.swapping = 'true';
        setTimeout(function () {
          pill.textContent = 'Search ' + terms[index] + '...';
          pill.removeAttribute('data-swapping');
        }, 220);
      }, 3000);
    });
  }

  function parseTerms(host) {
    try {
      var raw = host.dataset.placeholderTerms;
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  /* --------------------------------------------------------- desktop panel */

  var DesktopSearchPanel = function () {
    return Reflect.construct(HTMLElement, [], DesktopSearchPanel);
  };
  DesktopSearchPanel.prototype = Object.create(HTMLElement.prototype);
  DesktopSearchPanel.prototype.constructor = DesktopSearchPanel;
  Object.setPrototypeOf(DesktopSearchPanel, HTMLElement);

  DesktopSearchPanel.prototype.connectedCallback = function () {
    if (this._initialised) return;
    this._initialised = true;

    this.controller = new AbortController();
    var signal = this.controller.signal;
    var self = this;

    this.bar = document.querySelector('[data-thf-search-bar="desktop"]');
    if (!this.bar) return;

    this.core = new SearchCore(this, {});
    // The bar lives in the header, the panel at the end of <body>; wire the
    // header's input up as the panel's input.
    this.core.el.input = this.bar.querySelector('[data-thf-input]');
    this.core.el.form = this.bar.querySelector('[data-thf-form]');
    this.core.el.clear = this.bar.querySelector('[data-thf-clear]');
    this.core.bind(signal);

    var input = this.core.el.input;

    /* Below 1250px the bar is only ~104px wide, where "Search felt hats..."
       truncates to "SEARCH FE". Use a bare placeholder and stop rotating. */
    var narrow = window.matchMedia('(max-width: 1249px)');
    var fullPlaceholder = input.placeholder;

    function applyPlaceholder() {
      input.placeholder = narrow.matches ? 'Search' : fullPlaceholder;
    }

    applyPlaceholder();
    if (narrow.addEventListener) {
      narrow.addEventListener('change', applyPlaceholder, { signal: signal });
    }

    if (!narrow.matches) {
      startPlaceholderRotation(input, parseTerms(this), signal);
    }

    input.addEventListener('focus', function () {
      self.open();
    }, { signal: signal });

    input.addEventListener('click', function () {
      self.open();
    }, { signal: signal });

    // Typing after Escape should bring the panel back.
    this.core.onInputActivity = function () { self.open(); };

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.dataset.state === 'open') {
        self.close();
        input.focus();
      }
    }, { signal: signal });

    document.addEventListener('click', function (event) {
      if (self.dataset.state !== 'open') return;
      if (self.contains(event.target) || self.bar.contains(event.target)) return;
      self.close();
    }, { signal: signal });

    var overlay = this.querySelector('[data-thf-overlay]');
    if (overlay) {
      overlay.addEventListener('click', function () { self.close(); }, { signal: signal });
    }

    // Follow the header, which is sticky and changes height as it collapses.
    var reposition = function () { self.position(); };
    window.addEventListener('scroll', reposition, { passive: true, signal: signal });
    window.addEventListener('resize', reposition, { signal: signal });
    document.addEventListener('header:sticky-change', reposition, { signal: signal });
    this.position();
  };

  DesktopSearchPanel.prototype.disconnectedCallback = function () {
    if (this.controller) this.controller.abort();
    this._initialised = false;
  };

  DesktopSearchPanel.prototype.position = function () {
    var header = this.bar ? this.bar.closest('header') : null;
    if (!header) header = document.querySelector('header[data-desktop-header]');
    var bottom = header ? header.getBoundingClientRect().bottom : 0;
    document.documentElement.style.setProperty('--thf-search-panel-top', Math.max(0, bottom) + 'px');
  };

  DesktopSearchPanel.prototype.open = function () {
    if (this.dataset.state === 'open') return;
    this.position();
    this.dataset.state = 'open';
    this.core.renderRecent();
    this.core.syncToInput();
    if (this.bar) this.bar.dataset.focused = 'true';
  };

  DesktopSearchPanel.prototype.close = function () {
    this.dataset.state = 'closed';
    if (this.bar) this.bar.dataset.focused = 'false';
  };

  /* ---------------------------------------------------------- mobile drawer */

  var MobileSearchDrawer = function () {
    return Reflect.construct(HTMLElement, [], MobileSearchDrawer);
  };
  MobileSearchDrawer.prototype = Object.create(HTMLElement.prototype);
  MobileSearchDrawer.prototype.constructor = MobileSearchDrawer;
  Object.setPrototypeOf(MobileSearchDrawer, HTMLElement);

  MobileSearchDrawer.prototype.connectedCallback = function () {
    if (this._initialised) return;
    this._initialised = true;

    this.controller = new AbortController();
    var signal = this.controller.signal;
    var self = this;

    this.core = new SearchCore(this, {});
    this.core.bind(signal);
    startPlaceholderRotation(this.core.el.input, parseTerms(this), signal);

    var triggers = document.querySelectorAll('[data-thf-search-trigger]');
    Array.prototype.forEach.call(triggers, function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        self.open();
      }, { signal: signal });
    });

    // The header owns the logo; borrow it rather than duplicating the setting.
    var logoSlot = this.querySelector('[data-thf-logo]');
    var headerLogo = document.querySelector('.mobile_logo img, .header__logo img');
    if (logoSlot && headerLogo) {
      var clone = headerLogo.cloneNode(true);
      clone.removeAttribute('class');
      clone.removeAttribute('loading');
      if (!clone.getAttribute('src') && clone.dataset.src) clone.src = clone.dataset.src;
      clearChildren(logoSlot);
      logoSlot.appendChild(clone);
    }

    var close = this.querySelector('[data-thf-close]');
    if (close) {
      close.addEventListener('click', function () { self.close(); }, { signal: signal });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.dataset.state === 'open') self.close();
    }, { signal: signal });
  };

  MobileSearchDrawer.prototype.disconnectedCallback = function () {
    if (this.controller) this.controller.abort();
    this._initialised = false;
  };

  MobileSearchDrawer.prototype.open = function () {
    this.dataset.state = 'open';
    document.body.classList.add('thf-search-locked');
    this.core.renderRecent();
    this.core.syncToInput();
    var input = this.core.el.input;
    if (input) {
      // iOS only raises the keyboard for a focus inside the same task as the
      // tap, so keep this synchronous rather than deferring it.
      input.focus();
    }
  };

  MobileSearchDrawer.prototype.close = function () {
    this.dataset.state = 'closed';
    document.body.classList.remove('thf-search-locked');
  };

  customElements.define('thf-search-panel', DesktopSearchPanel);
  customElements.define('thf-search-drawer', MobileSearchDrawer);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startPillRotation);
  } else {
    startPillRotation();
  }
})();
